/**
 * Desk «پرونده‌ی کاربر» (owner OD-71, redesigned 2026-10-17 after the owner's review): the same facts as
 * the admin Customer 360, laid out for a phone first — a profile hero, then tabs (خلاصه · احراز هویت ·
 * دارایی · فعالیت). Only non-zero balances are listed (a toggle shows the rest). The KYC2 video is
 * fetched as private bytes on an explicit tap (audited server-side, device-signed step-up) and the
 * object URL is revoked on close. Every command goes through `ActForm` → the panel's own route.
 */
import { Avatar, cx, formatJalali, formatRelative } from '@hopex/ui';
import {
  ArrowRight,
  BadgeCheck,
  Check,
  Copy,
  Fingerprint,
  KeyRound,
  Lock,
  PlayCircle,
  ShieldCheck,
  Unlock,
  UserRound,
  Video,
  Wallet,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { ApiFailure, errorMessage, ops } from '../lib/api.ts';
import { navigate } from '../lib/platform.ts';
import {
  QueueCard,
  ReviewSheet,
  type CryptoDeposit,
  type CryptoWithdrawal,
  type Item,
  type TomanDeposit,
  type TomanWithdrawal,
  isOpen,
} from './ops-items.tsx';
import {
  ActButton,
  ActForm,
  amountOf,
  Empty,
  Facts,
  Loading,
  Panel,
  Pill,
  type Act,
  type Field,
  type Tone,
} from './ops-kit.tsx';

type CheckState = 'NOT_RUN' | 'PASSED' | 'FAILED' | 'UNAVAILABLE';
interface Kyc2Attempt {
  id: string;
  attemptNo: number;
  status: string;
  correctionTargets: string[] | null;
  decisionReason: string | null;
  evidence: { id: string; contentType: string; byteSize: number };
  submittedAt: string;
  decidedAt: string | null;
  kind?: 'SELFIE' | 'VIDEO';
  biometric?: {
    state: string;
    outcome: string | null;
    faceSimilarity: string | null;
    livenessScore: string | null;
    speechSimilarity: string | null;
    category: string | null;
    spokenLine: string | null;
  } | null;
}
interface UserDetail {
  user: {
    id: string;
    publicId: string;
    displayName: string | null;
    phoneMasked: string | null;
    status: string;
    createdAt: string;
    lastActiveAt: string | null;
  };
  security?: { totpEnabled: boolean; lastLoginAt: string | null };
  effectiveLevel: 'NONE' | 'KYC1' | 'KYC2';
  kyc: {
    status: string;
    reason: string | null;
    checks?: Record<string, CheckState>;
    verifiedAt: string | null;
    identity: { nationalIdMasked: string; legalName: string; birthYear: string } | null;
  } | null;
  kyc2: { status: string; reason: string | null; verifiedAt: string | null };
  kyc2Submissions?: Kyc2Attempt[];
}
interface Balance {
  asset: string;
  nameFa: string;
  available: string;
  reserved: string;
  total: string;
}
interface Finance {
  balances?: Balance[];
  deposits?: TomanDeposit[];
  withdrawals?: TomanWithdrawal[];
  adjustments?: {
    id: string;
    ref: string;
    operation: string;
    asset: string;
    amount: string;
    reason: string;
    createdAt: string;
  }[];
}
interface Can {
  balance: boolean;
  accounts: boolean;
  kyc: boolean;
}

const KYC1: Record<string, [string, Tone]> = {
  NOT_STARTED: ['انجام نشده', 'neutral'],
  IN_PROGRESS: ['انجام نشده', 'neutral'],
  PENDING: ['در انتظار بررسی', 'pending'],
  CHECKING: ['در حال بررسی', 'pending'],
  VERIFIED: ['تأییدشده', 'positive'],
  NEEDS_CORRECTION: ['نیازمند اصلاح', 'pending'],
  TEMPORARILY_UNAVAILABLE: ['خطای سرویس استعلام', 'pending'],
  REJECTED: ['ردشده', 'negative'],
};
const KYC2: Record<string, [string, Tone]> = {
  NOT_STARTED: ['انجام نشده', 'neutral'],
  SUBMITTED: ['در انتظار بررسی', 'pending'],
  PENDING: ['در انتظار بررسی', 'pending'],
  VERIFIED: ['تأییدشده', 'positive'],
  APPROVED: ['تأییدشده', 'positive'],
  NEEDS_CORRECTION: ['نیازمند اصلاح', 'pending'],
  REJECTED: ['ردشده', 'negative'],
  REVOKED: ['لغوشده', 'negative'],
  SUPERSEDED: ['جایگزین‌شده', 'neutral'],
};
const CHECK_LABEL: Record<string, string> = {
  phonePossession: 'مالکیت سیم‌کارت',
  mobileOwnership: 'تطابق موبایل با کد ملی',
  civilIdentity: 'تطابق با ثبت احوال',
  ageEligibility: 'سن ۱۸ سال تمام',
};
const BIO: Record<string, [string, Tone]> = {
  PASS: ['هر سه بررسی قبول', 'positive'],
  FACE_MISMATCH: ['چهره با ثبت احوال مطابقت ندارد', 'negative'],
  NOT_LIVE: ['زنده‌بودن تأیید نشد', 'negative'],
  SPEECH_MISMATCH: ['جمله درست خوانده نشد', 'pending'],
  IDENTITY_INVALID: ['هویت در ثبت احوال یافت نشد', 'negative'],
  CARD_EXPIRED: ['کارت ملی منقضی است', 'pending'],
  UNAVAILABLE: ['بررسی خودکار انجام نشد', 'neutral'],
  ERROR: ['خطای بررسی خودکار', 'neutral'],
};
const label = (m: Record<string, [string, Tone]>, s: string | null | undefined): [string, Tone] =>
  (s && m[s]) || ['—', 'neutral'];

type Tab = 'summary' | 'kyc' | 'assets' | 'activity';
const TABS: { id: Tab; label: string }[] = [
  { id: 'summary', label: 'خلاصه' },
  { id: 'kyc', label: 'احراز هویت' },
  { id: 'assets', label: 'دارایی' },
  { id: 'activity', label: 'فعالیت' },
];

export function UserOps({
  userId,
  tab: initialTab,
  can,
}: {
  userId: string;
  tab?: string | undefined;
  can: Can;
}) {
  const [d, setD] = useState<UserDetail | null>(null);
  const [fin, setFin] = useState<Finance | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>(
    TABS.some((t) => t.id === initialTab) ? (initialTab as Tab) : 'summary',
  );
  const [act, setAct] = useState<Act | null>(null);
  const load = useCallback(() => {
    ops
      .get<UserDetail>(`users/${userId}`)
      .then((x) => {
        setD(x);
        setErr(null);
      })
      .catch((e: unknown) => setErr(errorMessage(e)));
    ops
      .get<Finance>(`users/${userId}/finance`)
      .then(setFin)
      .catch(() => setFin({}));
  }, [userId]);
  useEffect(() => {
    const t = window.setTimeout(load, 0);
    return () => window.clearTimeout(t);
  }, [load]);

  const pick = (a: Act) => {
    setAct(a);
    window.setTimeout(
      () => document.getElementById('ops-act')?.scrollIntoView({ block: 'center' }),
      0,
    );
  };
  const actPanel = act && (
    <Panel title={act.label} className="border-accent/50" data-ops-act-panel>
      <div id="ops-act">
        <ActForm
          act={act}
          onCancel={() => setAct(null)}
          onDone={() => {
            setAct(null);
            load();
          }}
        />
      </div>
    </Panel>
  );

  return (
    <>
      <header className="flex shrink-0 items-center gap-2 border-b border-line bg-surface-1 px-3 py-2.5">
        <button
          type="button"
          // Back to where the agent came from (a chat, a queue), else the operations home.
          onClick={() => (window.history.length > 1 ? window.history.back() : navigate('/ops'))}
          aria-label="بازگشت"
          className="hx-press grid size-10 cursor-pointer place-items-center rounded-full text-fg-muted hover:bg-surface-2"
        >
          <ArrowRight aria-hidden className="size-5" />
        </button>
        <h2 className="m-0 min-w-0 flex-1 truncate text-[15px] font-extrabold">پرونده‌ی کاربر</h2>
      </header>
      <div className="min-h-0 flex-1 overflow-y-auto pb-[calc(var(--hx-sab,env(safe-area-inset-bottom))+20px)]">
        {err && (
          <p role="alert" className="m-0 mt-8 px-4 text-center text-[13px] text-negative">
            {err}
          </p>
        )}
        {!d && !err && (
          <div className="mx-auto max-w-5xl p-3">
            <Loading />
          </div>
        )}
        {d && (
          <div className="mx-auto flex max-w-5xl flex-col gap-3 p-3" data-ops-user={userId}>
            <Hero d={d} />
            <nav
              className="sticky top-0 z-10 -mx-3 flex gap-1 overflow-x-auto bg-canvas/90 px-3 py-1.5 backdrop-blur"
              role="tablist"
              aria-label="بخش‌های پرونده"
            >
              {TABS.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  role="tab"
                  aria-selected={tab === t.id}
                  onClick={() => {
                    setTab(t.id);
                    setAct(null);
                  }}
                  data-ops-tab={t.id}
                  className={cx(
                    'hx-press min-h-10 shrink-0 cursor-pointer rounded-full px-4 text-[13px] font-bold transition-colors',
                    tab === t.id
                      ? 'bg-accent text-on-accent shadow-cta'
                      : 'bg-surface-1 text-fg-muted hover:text-fg',
                  )}
                >
                  {t.label}
                </button>
              ))}
            </nav>
            {tab === 'summary' && <Summary d={d} fin={fin} can={can} onAct={pick} />}
            {tab === 'kyc' && <KycTab d={d} can={can} onAct={pick} />}
            {tab === 'assets' && <AssetsTab userId={userId} fin={fin} can={can} onAct={pick} />}
            {tab === 'activity' && <ActivityTab userId={userId} fin={fin} onChanged={load} />}
            {actPanel}
          </div>
        )}
      </div>
    </>
  );
}

function CopyId({ value }: { value: string }) {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      onClick={() => {
        void navigator.clipboard?.writeText(value).then(() => {
          setDone(true);
          window.setTimeout(() => setDone(false), 1500);
        });
      }}
      className="inline-flex cursor-pointer items-center gap-1 rounded-full bg-white/10 px-2 py-0.5 text-[11.5px] font-bold text-fg"
      aria-label="کپی شناسه"
    >
      <bdi dir="ltr">{value}</bdi>
      {done ? <Check aria-hidden className="size-3" /> : <Copy aria-hidden className="size-3" />}
    </button>
  );
}

function Hero({ d }: { d: UserDetail }) {
  const active = d.user.status === 'ACTIVE';
  const level =
    d.effectiveLevel === 'KYC2' ? 'سطح ۲' : d.effectiveLevel === 'KYC1' ? 'سطح ۱' : 'بدون احراز';
  return (
    <section
      data-theme-scope="dark"
      className="relative overflow-hidden rounded-[26px] border border-line-strong bg-canvas-deep p-4 text-fg sm:p-5"
    >
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(80%_140%_at_100%_0%,color-mix(in_srgb,var(--hx-accent)_26%,transparent),transparent_62%)]"
      />
      <div className="relative flex items-center gap-3.5">
        <Avatar name={d.user.displayName} size="lg" verified={d.effectiveLevel !== 'NONE'} />
        <div className="min-w-0 flex-1">
          <p className="m-0 truncate text-[18px] font-black sm:text-[20px]">
            {d.user.displayName ?? d.kyc?.identity?.legalName ?? 'بدون نام'}
          </p>
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            <CopyId value={d.user.publicId} />
            <bdi dir="ltr" className="text-[12px] text-fg-muted">
              {d.user.phoneMasked ?? '—'}
            </bdi>
          </div>
        </div>
      </div>
      <div className="relative mt-3.5 flex flex-wrap gap-1.5">
        <Pill tone={d.effectiveLevel === 'NONE' ? 'neutral' : 'accent'}>
          <BadgeCheck aria-hidden className="size-3.5" /> {level}
        </Pill>
        <Pill tone={active ? 'positive' : 'negative'}>
          {active ? (
            <Unlock aria-hidden className="size-3.5" />
          ) : (
            <Lock aria-hidden className="size-3.5" />
          )}
          {active ? 'حساب فعال' : 'حساب مسدود'}
        </Pill>
        {d.security && (
          <Pill tone={d.security.totpEnabled ? 'positive' : 'neutral'}>
            <KeyRound aria-hidden className="size-3.5" />
            {d.security.totpEnabled ? 'ورود دومرحله‌ای فعال' : 'بدون ورود دومرحله‌ای'}
          </Pill>
        )}
      </div>
    </section>
  );
}

function Summary({
  d,
  fin,
  can,
  onAct,
}: {
  d: UserDetail;
  fin: Finance | null;
  can: Can;
  onAct: (a: Act) => void;
}) {
  const [k1, k1t] = label(KYC1, d.kyc?.status ?? 'NOT_STARTED');
  const [k2, k2t] = label(KYC2, d.kyc2.status);
  const nonZero = (fin?.balances ?? []).filter((b) => !isZero(b.total));
  const reason: Field = { key: 'reason', label: 'دلیل (در سابقه ثبت می‌شود)', required: true };
  const locked = d.user.status !== 'ACTIVE';
  return (
    <>
      <div className="grid gap-3 lg:grid-cols-[1.2fr_1fr]">
        <Panel title="مشخصات حساب" icon={<UserRound aria-hidden className="size-4" />}>
          <Facts
            rows={[
              ['نام ثبت‌شده', d.kyc?.identity?.legalName ?? '—'],
              [
                'کد ملی',
                d.kyc?.identity ? (
                  <bdi dir="ltr" key="n">
                    {d.kyc.identity.nationalIdMasked}
                  </bdi>
                ) : (
                  '—'
                ),
              ],
              ['عضویت', formatJalali(d.user.createdAt)],
              ['آخرین فعالیت', d.user.lastActiveAt ? formatRelative(d.user.lastActiveAt) : '—'],
            ]}
          />
        </Panel>
        <Panel title="احراز هویت" icon={<ShieldCheck aria-hidden className="size-4" />}>
          <div className="grid grid-cols-2 gap-2">
            <Stat label="سطح ۱" value={<Pill tone={k1t}>{k1}</Pill>} />
            <Stat label="سطح ۲" value={<Pill tone={k2t}>{k2}</Pill>} />
            <Stat
              label="دارایی‌های دارای موجودی"
              value={
                <span className="hx-num text-[18px] font-black">
                  {nonZero.length.toLocaleString('fa-IR')}
                </span>
              }
            />
            <Stat
              label="ورود دومرحله‌ای"
              value={
                <Pill tone={d.security?.totpEnabled ? 'positive' : 'neutral'}>
                  {d.security?.totpEnabled ? 'فعال' : 'غیرفعال'}
                </Pill>
              }
            />
          </div>
        </Panel>
      </div>
      {can.accounts && (
        <Panel title="وضعیت حساب" icon={<Lock aria-hidden className="size-4" />}>
          <div className="grid gap-2 sm:grid-cols-2">
            <ActButton
              onPick={onAct}
              a={{
                id: 'status',
                label: locked ? 'فعال کردن حساب' : 'مسدود کردن حساب',
                hint: locked ? 'کاربر دوباره می‌تواند وارد شود' : 'ورود و همه‌ی کارها متوقف می‌شود',
                tone: locked ? 'positive' : 'negative',
                fields: [reason],
                run: (v) =>
                  ops.run(`users/${d.user.id}/account/status`, {
                    status: locked ? 'ACTIVE' : 'LOCKED',
                    reason: v.reason,
                  }),
              }}
            />
          </div>
        </Panel>
      )}
    </>
  );
}

function Stat({ label: l, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex min-h-[72px] flex-col justify-between gap-1.5 rounded-[16px] bg-surface-2 p-3">
      <span className="text-[11.5px] text-fg-muted">{l}</span>
      <span>{value}</span>
    </div>
  );
}

// ───────── KYC ─────────
function KycTab({ d, can, onAct }: { d: UserDetail; can: Can; onAct: (a: Act) => void }) {
  const [k1, k1t] = label(KYC1, d.kyc?.status ?? 'NOT_STARTED');
  const [k2, k2t] = label(KYC2, d.kyc2.status);
  const reason: Field = { key: 'reason', label: 'دلیل (به کاربر و در سابقه)', required: true };
  const kycAct = (
    action: string,
    l: string,
    tone: Act['tone'],
    extra: Record<string, unknown> = {},
  ): Act => ({
    id: action,
    label: l,
    tone,
    fields: [reason],
    run: (v) => ops.run(`users/${d.user.id}/kyc/actions`, { action, reason: v.reason, ...extra }),
  });
  const attempts = d.kyc2Submissions ?? [];
  const latest = attempts[0];
  const k1Open = d.kyc && d.kyc.status !== 'VERIFIED';
  const k2Open = latest?.status === 'SUBMITTED';
  return (
    <div className="grid gap-3 lg:grid-cols-2">
      <Panel
        title="احراز سطح ۱"
        icon={<Fingerprint aria-hidden className="size-4" />}
        aside={<Pill tone={k1t}>{k1}</Pill>}
        data-ops-kyc1
      >
        {d.kyc?.identity ? (
          <Facts
            rows={[
              ['نام و نام خانوادگی', d.kyc.identity.legalName],
              [
                'کد ملی',
                <bdi dir="ltr" key="n">
                  {d.kyc.identity.nationalIdMasked}
                </bdi>,
              ],
              [
                'سال تولد',
                <span key="y" className="hx-num">
                  {d.kyc.identity.birthYear}
                </span>,
              ],
              ['تأیید', d.kyc.verifiedAt ? formatJalali(d.kyc.verifiedAt) : '—'],
            ]}
          />
        ) : (
          <Empty>اطلاعات سطح ۱ هنوز ثبت نشده است.</Empty>
        )}
        {d.kyc?.checks && (
          <ul className="m-0 mt-3 grid list-none gap-1.5 p-0 sm:grid-cols-2">
            {Object.entries(d.kyc.checks).map(([k, s]) => (
              <li
                key={k}
                className="flex items-center gap-2 rounded-[12px] bg-surface-2 px-3 py-2 text-[12.5px]"
              >
                {s === 'PASSED' ? (
                  <Check aria-hidden className="size-4 text-positive" />
                ) : s === 'FAILED' ? (
                  <X aria-hidden className="size-4 text-negative" />
                ) : (
                  <span aria-hidden className="size-4 text-center text-fg-subtle">
                    –
                  </span>
                )}
                <span className="min-w-0 flex-1">{CHECK_LABEL[k] ?? k}</span>
                <span className="sr-only">
                  {s === 'PASSED' ? 'تأیید' : s === 'FAILED' ? 'رد' : 'بررسی نشده'}
                </span>
              </li>
            ))}
          </ul>
        )}
        {d.kyc?.reason && (
          <p className="m-0 mt-3 rounded-[12px] bg-surface-2 px-3 py-2 text-[12.5px] text-fg-muted">
            دلیل: {d.kyc.reason}
          </p>
        )}
        {can.kyc && k1Open && (
          <div className="mt-3 grid grid-cols-2 gap-2">
            <ActButton a={kycAct('KYC1_APPROVE', 'تأیید سطح ۱', 'positive')} onPick={onAct} />
            <ActButton a={kycAct('KYC1_REJECT', 'رد سطح ۱', 'negative')} onPick={onAct} />
          </div>
        )}
      </Panel>
      <Panel
        title="احراز سطح ۲ (ویدیو)"
        icon={<Video aria-hidden className="size-4" />}
        aside={<Pill tone={k2t}>{k2}</Pill>}
        data-ops-kyc2
      >
        {attempts.length === 0 ? (
          <Empty>کاربر هنوز ویدیوی سطح ۲ نفرستاده است.</Empty>
        ) : (
          <ol className="m-0 flex list-none flex-col gap-3 p-0">
            {attempts.map((s, i) => (
              <Attempt key={s.id} userId={d.user.id} s={s} open={i === 0} />
            ))}
          </ol>
        )}
        {can.kyc && k2Open && (
          <div className="mt-3 grid grid-cols-2 gap-2">
            <ActButton
              a={{ ...kycAct('KYC2_APPROVE', 'تأیید سطح ۲', 'positive') }}
              onPick={onAct}
            />
            <ActButton
              a={kycAct('KYC2_RETURN', 'ضبط دوباره‌ی ویدیو', 'neutral', {
                correctionTargets: ['SELFIE'],
              })}
              onPick={onAct}
            />
            <ActButton a={kycAct('KYC2_REJECT', 'رد سطح ۲', 'negative')} onPick={onAct} />
          </div>
        )}
      </Panel>
    </div>
  );
}

function Attempt({ userId, s, open }: { userId: string; s: Kyc2Attempt; open: boolean }) {
  const [st, stt] = label(KYC2, s.status);
  const b = s.biometric;
  const [bio, bioT]: [string, Tone] =
    b?.category === 'DAILY_CAP'
      ? ['سقف ۳ بررسی خودکار در ۲۴ ساعت پر بود — بررسی دستی کنید', 'pending']
      : /کیف پول/.test(b?.category ?? '')
        ? ['کیف پول جیبیت خالی بود — بررسی دستی کنید', 'pending']
        : b?.outcome
          ? label(BIO, b.outcome)
          : ['', 'neutral'];
  const pct = (v: string | null) => (v === null ? '—' : `${Number(v).toLocaleString('fa-IR')}٪`);
  return (
    <li
      className="flex flex-col gap-2.5 rounded-[18px] border border-line bg-surface-2/60 p-3"
      data-kyc2-attempt={s.id}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[13px] font-extrabold">
          تلاش {s.attemptNo.toLocaleString('fa-IR')}
        </span>
        <Pill tone={stt}>{st}</Pill>
        <span className="ms-auto text-[11.5px] text-fg-muted">{formatRelative(s.submittedAt)}</span>
      </div>
      <Evidence userId={userId} s={s} autoOpen={open} />
      {b && b.state !== 'NONE' && (
        <div className="flex flex-col gap-1.5 rounded-[14px] bg-surface-1 p-2.5">
          <span className="flex flex-wrap items-center gap-1.5 text-[12px] font-bold">
            بررسی خودکار جیبیت:
            {b.state === 'QUEUED' || b.state === 'RUNNING' ? (
              <Pill tone="pending">در حال بررسی</Pill>
            ) : (
              <Pill tone={bioT}>{bio || 'نامشخص'}</Pill>
            )}
          </span>
          <div className="grid grid-cols-3 gap-1.5 text-center">
            {(
              [
                ['چهره', b.faceSimilarity],
                ['زنده‌بودن', b.livenessScore],
                ['گفتار', b.speechSimilarity],
              ] as const
            ).map(([k, v]) => (
              <span key={k} className="rounded-[10px] bg-surface-2 px-1 py-1.5">
                <span className="block text-[10.5px] text-fg-muted">{k}</span>
                <span className="hx-num block text-[13px] font-black">{pct(v)}</span>
              </span>
            ))}
          </div>
          {b.spokenLine && (
            <span className="text-[12px] text-fg-muted">جمله: «{b.spokenLine}»</span>
          )}
        </div>
      )}
      {s.decisionReason && (
        <p className="m-0 text-[12px] text-fg-muted">دلیل تصمیم: {s.decisionReason}</p>
      )}
    </li>
  );
}

function Evidence({ userId, s, autoOpen }: { userId: string; s: Kyc2Attempt; autoOpen: boolean }) {
  const [url, setUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  useEffect(
    () => () => {
      if (url) URL.revokeObjectURL(url);
    },
    [url],
  );
  const video = s.evidence.contentType.startsWith('video/');
  const show = async () => {
    setBusy(true);
    setErr(null);
    try {
      const blob = await ops.blob(`users/${userId}/kyc2/evidence/${s.evidence.id}`, {
        mode: 'VIEW',
      });
      setUrl(URL.createObjectURL(blob));
    } catch (e) {
      setErr(e instanceof ApiFailure ? e.message : errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  if (url)
    return (
      <div className="flex flex-col gap-1.5">
        {video ? (
          <video
            src={url}
            controls
            playsInline
            autoPlay={autoOpen}
            className="aspect-[3/4] max-h-[26rem] w-full rounded-[16px] bg-black object-contain"
            aria-label={`ویدیوی احراز سطح ۲، تلاش ${s.attemptNo}`}
            data-kyc2-video
          />
        ) : (
          <img
            src={url}
            alt={`مدرک احراز سطح ۲، تلاش ${s.attemptNo}`}
            className="max-h-[26rem] w-full rounded-[16px] bg-black object-contain"
          />
        )}
        <button
          type="button"
          onClick={() => setUrl(null)}
          className="w-fit cursor-pointer text-[12px] font-bold text-fg-muted"
        >
          بستن
        </button>
      </div>
    );
  return (
    <div className="flex flex-col gap-1.5">
      <button
        type="button"
        onClick={() => void show()}
        disabled={busy}
        data-kyc2-view
        className="hx-press group relative grid aspect-[16/9] w-full cursor-pointer place-items-center overflow-hidden rounded-[16px] border border-line-strong bg-canvas-deep text-fg disabled:opacity-70"
        data-theme-scope="dark"
      >
        <span
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(60%_90%_at_50%_40%,color-mix(in_srgb,var(--hx-accent)_22%,transparent),transparent_70%)]"
        />
        <span className="relative flex flex-col items-center gap-1.5">
          <PlayCircle
            aria-hidden
            className="size-12 text-accent-text transition-transform group-hover:scale-110"
          />
          <span className="text-[13px] font-extrabold">
            {busy ? 'در حال دریافت…' : video ? 'پخش ویدیو' : 'نمایش مدرک'}
          </span>
          <span className="text-[11px] text-fg-muted">
            <bdi dir="ltr" className="hx-num">
              {(s.evidence.byteSize / 1_048_576).toFixed(1)} MB
            </bdi>{' '}
            · هر مشاهده ثبت می‌شود
          </span>
        </span>
      </button>
      {err && (
        <p role="alert" className="m-0 text-[12px] text-negative">
          {err}
        </p>
      )}
    </div>
  );
}

// ───────── assets ─────────
const isZero = (v: string) => /^0*(\.0*)?$/.test(v.replace('-', ''));

function AssetsTab({
  userId,
  fin,
  can,
  onAct,
}: {
  userId: string;
  fin: Finance | null;
  can: Can;
  onAct: (a: Act) => void;
}) {
  const [all, setAll] = useState(false);
  const balances = useMemo(() => fin?.balances ?? [], [fin]);
  const shown = all ? balances : balances.filter((b) => !isZero(b.total));
  const hidden = balances.length - balances.filter((b) => !isZero(b.total)).length;
  const reason: Field = { key: 'reason', label: 'دلیل', required: true };
  const assets = [
    { value: 'IRT', label: 'تومان' },
    ...balances
      .filter(
        (b) =>
          b.asset !== 'IRT' &&
          (!isZero(b.total) || ['USDT', 'BTC', 'ETH', 'TRX', 'BNB'].includes(b.asset)),
      )
      .map((b) => ({ value: b.asset, label: b.asset })),
  ];
  const adj = (operation: 'CREDIT' | 'DEBIT'): Act => ({
    id: operation,
    label: operation === 'CREDIT' ? 'افزایش موجودی' : 'کاهش موجودی',
    hint: 'با سند و دلیل؛ در دفتر کل ثبت می‌شود',
    tone: operation === 'CREDIT' ? 'positive' : 'negative',
    choices: { key: 'asset', label: 'دارایی', options: assets },
    fields: [
      { key: 'amount', label: 'مقدار', ltr: true, required: true },
      { key: 'ref', label: 'شماره سند / پیگیری', ltr: true, required: true },
      reason,
    ],
    run: async (v) => {
      const body = { operation, asset: v.asset, amount: v.amount };
      const p = await ops.run<{
        before: { available: string; reserved: string };
        blocked: string | null;
      }>(`users/${userId}/adjustments/preview`, body);
      if (p.blocked)
        throw new ApiFailure(
          'BLOCKED',
          'این تغییر مجاز نیست (موجودی کافی نیست یا مقدار نامعتبر است).',
          422,
        );
      return ops.run(`users/${userId}/adjustments`, {
        ...body,
        reason: v.reason,
        externalReference: v.ref,
        expectedAvailable: p.before.available,
        expectedReserved: p.before.reserved,
        confirm: true,
      });
    },
  });
  return (
    <>
      <Panel
        title="دارایی‌ها"
        icon={<Wallet aria-hidden className="size-4" />}
        aside={
          hidden > 0 && (
            <button
              type="button"
              onClick={() => setAll((x) => !x)}
              className="cursor-pointer rounded-full bg-surface-2 px-3 py-1 text-[11.5px] font-bold text-fg-muted"
              data-ops-all-assets
            >
              {all
                ? 'فقط دارای موجودی'
                : `همه (${(hidden + shown.length).toLocaleString('fa-IR')})`}
            </button>
          )
        }
        data-ops-assets
      >
        {!fin ? (
          <Loading />
        ) : shown.length === 0 ? (
          <Empty>این کاربر موجودی ندارد.</Empty>
        ) : (
          <ul className="m-0 grid list-none gap-2 p-0 sm:grid-cols-2 lg:grid-cols-3">
            {shown.map((b) => (
              <li
                key={b.asset}
                className="flex items-center gap-3 rounded-[18px] border border-line bg-surface-2/60 p-3"
                data-ops-balance={b.asset}
              >
                <span className="grid size-10 shrink-0 place-items-center rounded-full bg-accent-tint text-[10.5px] font-black text-accent-text">
                  {b.asset === 'IRT' ? 'ت' : b.asset.slice(0, 4)}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[12px] text-fg-muted">{b.nameFa}</span>
                  <span className="block truncate text-[15px] font-black">
                    {amountOf(b.available, b.asset)}
                  </span>
                  {!isZero(b.reserved) && (
                    <span className="block text-[11px] text-[var(--hx-pending)]">
                      رزرو: {amountOf(b.reserved, b.asset)}
                    </span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Panel>
      {can.balance && (
        <div className="grid grid-cols-2 gap-2">
          <ActButton a={adj('CREDIT')} onPick={onAct} />
          <ActButton a={adj('DEBIT')} onPick={onAct} />
        </div>
      )}
    </>
  );
}

/**
 * The person's deposits and withdrawals with approve / reject, for the chat's «مشاهده پرونده» panel
 * (owner 2026-10-17): loads its own data; nothing is shown without the operations permissions.
 */
export function UserMoneyPanel({ userId }: { userId: string }) {
  const [fin, setFin] = useState<Finance | null>(null);
  const load = useCallback(() => {
    ops
      .get<Finance>(`users/${userId}/finance`)
      .then(setFin)
      .catch(() => setFin({}));
  }, [userId]);
  useEffect(() => {
    const t = window.setTimeout(load, 0);
    return () => window.clearTimeout(t);
  }, [load]);
  return <ActivityTab userId={userId} fin={fin} onChanged={load} compact />;
}

// ───────── activity ─────────
/**
 * Every deposit / withdrawal of this person — Toman from the ledger read model, crypto from the
 * queues filtered by user — as the same cards as the queues; a tap opens the review sheet (commands
 * only while the item still waits). Manual balance changes are listed below them.
 */
function ActivityTab({
  userId,
  fin,
  onChanged,
  compact = false,
}: {
  userId: string;
  fin: Finance | null;
  onChanged: () => void;
  /** Narrow side panel (chat profile): one column. */
  compact?: boolean;
}) {
  const [crypto, setCrypto] = useState<Item[] | null>(null);
  const [open, setOpen] = useState<Item | null>(null);
  const loadCrypto = useCallback(() => {
    const pick = <T,>(r: { items: T[] }) => r.items;
    void Promise.all([
      ops
        .get<{ items: CryptoDeposit[] }>(`crypto-deposits?userId=${userId}`)
        .then((r) =>
          pick(r)
            .filter((v) => v.status !== 'ADDRESS_ISSUED')
            .map((v) => ({ k: 'CD' as const, v })),
        )
        .catch(() => [] as Item[]),
      ops
        .get<{ items: CryptoWithdrawal[] }>(`crypto-withdrawals?userId=${userId}`)
        .then((r) => pick(r).map((v) => ({ k: 'CW' as const, v })))
        .catch(() => [] as Item[]),
    ]).then(([a, b]) => setCrypto([...a, ...b]));
  }, [userId]);
  useEffect(() => {
    const t = window.setTimeout(loadCrypto, 0);
    return () => window.clearTimeout(t);
  }, [loadCrypto]);
  const items = useMemo(() => {
    if (!fin || !crypto) return null;
    const out: Item[] = [
      ...(fin.deposits ?? []).map((v) => ({ k: 'TD' as const, v })),
      ...(fin.withdrawals ?? []).map((v) => ({ k: 'TW' as const, v })),
      ...crypto,
    ];
    // What waits for a decision first, then the newest.
    return out.sort(
      (a, b) =>
        Number(isOpen(b)) - Number(isOpen(a)) ||
        (b.k === 'KYC' ? '' : b.v.createdAt).localeCompare(a.k === 'KYC' ? '' : a.v.createdAt),
    );
  }, [fin, crypto]);
  const adjustments = fin?.adjustments ?? [];
  return (
    <>
      <Panel
        title="واریز و برداشت‌ها"
        icon={<Wallet aria-hidden className="size-4" />}
        data-ops-activity
      >
        {!items ? (
          <Loading />
        ) : items.length === 0 ? (
          <Empty>واریز یا برداشتی ثبت نشده است.</Empty>
        ) : (
          <ul className={cx('m-0 grid list-none gap-2.5 p-0', !compact && 'lg:grid-cols-2')}>
            {items.map((it) => (
              <li key={`${it.k}-${it.v.id}`}>
                <QueueCard it={it} onOpen={() => setOpen(it)} />
              </li>
            ))}
          </ul>
        )}
      </Panel>
      {adjustments.length > 0 && (
        <Panel title="تغییرهای دستی موجودی" icon={<ShieldCheck aria-hidden className="size-4" />}>
          <ul className="m-0 flex list-none flex-col p-0">
            {adjustments.map((x) => (
              <li
                key={x.id}
                className="flex items-center gap-3 border-b border-line/70 py-2.5 last:border-b-0"
              >
                <span className="min-w-0 flex-1">
                  <span className="block text-[13px] font-bold">
                    {x.operation === 'DEBIT' ? 'کاهش موجودی' : 'افزایش موجودی'}
                  </span>
                  <span className="block truncate text-[11.5px] text-fg-muted">
                    <bdi dir="ltr">{x.ref}</bdi> · {formatRelative(x.createdAt)} · {x.reason}
                  </span>
                </span>
                <span className="shrink-0 text-[13px] font-extrabold">
                  {amountOf(x.amount, x.asset)}
                </span>
              </li>
            ))}
          </ul>
        </Panel>
      )}
      {open && (
        <ReviewSheet
          item={open}
          showUserLink={false}
          onClose={() => setOpen(null)}
          onDone={() => {
            setOpen(null);
            onChanged();
            loadCrypto();
          }}
        />
      )}
    </>
  );
}
